import { Helmet } from "react-helmet-async";
import { BUSINESS } from "@/data/businessInfo";

export const SEO = ({ title, description, path = "", schema = null, noindex = false }) => {
  const siteUrl = "https://southernlightfl.com";
  // Title format: "Primary Keyword - Secondary | Brand" for max SEO
  const fullTitle = title || "Electrician Port St. Lucie FL - Residential & Commercial | Southern Light Solutions";
  const desc = description || "Southern Light Solutions LLC - Licensed electrician in Port St. Lucie, FL serving the Treasure Coast. EV chargers, generators, panel upgrades, lighting. 5-star rated, BBB A-rated. Call (772) 233-6472.";

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <link rel="canonical" href={`${siteUrl}${path}`} />

      {/* Geo meta tags for local SEO */}
      <meta name="geo.region" content="US-FL" />
      <meta name="geo.placename" content="Port St. Lucie, Florida" />
      <meta name="geo.position" content="27.298911;-80.405438" />
      <meta name="ICBM" content="27.298911, -80.405438" />

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={`${siteUrl}${path}`} />
      <meta property="og:image" content="https://southernlightfl.com/og-image.png" />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:type" content="image/png" />
      <meta property="og:image:alt" content={`${BUSINESS.name} - Licensed Electrician`} />
      <meta property="og:locale" content="en_US" />
      <meta property="og:site_name" content={BUSINESS.name} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content="https://southernlightfl.com/og-image.png" />

      {/* Additional SEO */}
      <meta name="robots" content={noindex ? "noindex, follow" : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"} />
      <meta name="author" content={BUSINESS.name} />
      <meta name="publisher" content={BUSINESS.name} />

      {/* Inline schema if provided */}
      {schema && <script type="application/ld+json">{JSON.stringify(schema)}</script>}
    </Helmet>
  );
};

// Main business schema - goes on every page via App.js
export const LocalBusinessSchema = () => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Electrician",
    "@id": "https://southernlightfl.com/#business",
    "name": BUSINESS.name,
    "alternateName": "Southern Light Solutions",
    "image": BUSINESS.logo,
    "telephone": BUSINESS.phone,
    "email": BUSINESS.email,
    "url": "https://southernlightfl.com",
    "priceRange": "$$",
    "description": "Licensed and insured electrical contractor serving the Treasure Coast and Palm Beach County. Residential and commercial electrical services including EV charger installation, generator installation, panel upgrades, lighting, and more.",
    "slogan": "Lighting the Way for All Your Current Needs",
    "foundingDate": "2024",
    "founder": { "@type": "Person", "name": "Anthony Mazza" },
    "numberOfEmployees": { "@type": "QuantitativeValue", "value": "1-10" },
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Port St. Lucie",
      "addressRegion": "FL",
      "postalCode": "34986",
      "addressCountry": "US"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": 27.298911,
      "longitude": -80.405438
    },
    "areaServed": [
      { "@type": "City", "name": "Port St. Lucie", "sameAs": "https://en.wikipedia.org/wiki/Port_St._Lucie,_Florida" },
      { "@type": "City", "name": "Fort Pierce" },
      { "@type": "City", "name": "Stuart" },
      { "@type": "City", "name": "Vero Beach" },
      { "@type": "City", "name": "Sebastian" },
      { "@type": "City", "name": "Jensen Beach" },
      { "@type": "City", "name": "Palm City" },
      { "@type": "City", "name": "West Palm Beach" },
      { "@type": "City", "name": "Jupiter" },
      { "@type": "City", "name": "Palm Beach Gardens" },
      { "@type": "AdministrativeArea", "name": "St. Lucie County, FL" },
      { "@type": "AdministrativeArea", "name": "Martin County, FL" },
      { "@type": "AdministrativeArea", "name": "Indian River County, FL" },
      { "@type": "AdministrativeArea", "name": "Palm Beach County, FL" }
    ],
    "openingHoursSpecification": [
      { "@type": "OpeningHoursSpecification", "dayOfWeek": ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"], "opens": "00:00", "closes": "23:59" }
    ],
    "sameAs": [
      BUSINESS.social.facebook,
      BUSINESS.social.yelp,
      BUSINESS.social.bbb,
      BUSINESS.social.nextdoor,
      BUSINESS.social.google
    ],
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Electrical Services",
      "itemListElement": [
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "EV Charger Installation", "description": "Level 2 residential and commercial EV charging station installation" } },
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Generator Installation & Repair", "description": "Whole-home and commercial standby generator installation and maintenance" } },
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Electrical Panel Upgrades", "description": "100-to-200-amp panel upgrades and complete service entrance replacements" } },
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Lighting Installation", "description": "Interior, exterior, and landscape lighting design and installation" } },
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Ceiling Fan Installation", "description": "Ceiling fan installation, replacement, and wiring for all room types" } },
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Code Corrections & Compliance", "description": "Electrical code violation corrections and compliance upgrades" } },
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Electrical Troubleshooting & Repair", "description": "Fast diagnosis and repair of all residential and commercial electrical problems" } },
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "AV & Display Setup", "description": "Professional wiring for AV systems, TV displays, and digital signage" } }
      ]
    },
    "knowsAbout": ["Electrical wiring", "EV charger installation", "Generator installation", "Electrical panel upgrade", "Lighting installation", "Ceiling fan installation", "Code corrections", "Electrical troubleshooting", "Commercial electrical", "Residential electrical"],
    "paymentAccepted": "Cash, Check, Credit Card",
    "currenciesAccepted": "USD"
  };

  // WebSite schema for sitelinks searchbox
  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": BUSINESS.name,
    "url": "https://southernlightfl.com",
    "potentialAction": {
      "@type": "SearchAction",
      "target": "https://southernlightfl.com/services?q={search_term_string}",
      "query-input": "required name=search_term_string"
    }
  };

  // BreadcrumbList for homepage
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://southernlightfl.com/" },
      { "@type": "ListItem", "position": 2, "name": "Services", "item": "https://southernlightfl.com/services" },
      { "@type": "ListItem", "position": 3, "name": "Service Areas", "item": "https://southernlightfl.com/service-areas" },
      { "@type": "ListItem", "position": 4, "name": "Contact", "item": "https://southernlightfl.com/contact" }
    ]
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
      <script type="application/ld+json">{JSON.stringify(websiteSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
    </Helmet>
  );
};

// Service-specific schema for individual service pages
export const ServiceSchema = ({ serviceName, serviceDescription, serviceType }) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": serviceName,
    "description": serviceDescription,
    "provider": {
      "@type": "Electrician",
      "name": BUSINESS.name,
      "telephone": BUSINESS.phone,
      "address": { "@type": "PostalAddress", "addressLocality": "Port St. Lucie", "addressRegion": "FL", "addressCountry": "US" }
    },
    "areaServed": { "@type": "State", "name": "Florida" },
    "serviceType": serviceType || "Electrical Service",
    "offers": {
      "@type": "Offer",
      "availability": "https://schema.org/InStock",
      "priceCurrency": "USD",
      "description": "Free estimates available"
    }
  };
  return <Helmet><script type="application/ld+json">{JSON.stringify(schema)}</script></Helmet>;
};

// FAQ schema for pages with FAQ sections
export const FAQSchema = ({ faqs }) => {
  if (!faqs || faqs.length === 0) return null;
  const schema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(f => ({
      "@type": "Question",
      "name": f.q,
      "acceptedAnswer": { "@type": "Answer", "text": f.a }
    }))
  };
  return <Helmet><script type="application/ld+json">{JSON.stringify(schema)}</script></Helmet>;
};
